import axios, { AxiosResponse } from 'axios'

import { ResponseSuccess } from '@models/apiBase'
import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { AppointmentModel } from '@models/appointment'
import { ID } from '@models/base'

import { API_URL } from '@/constants/constant'

type ResponseAppointment = Response<{ appointment: AppointmentModel }>

type ResponseAppointmentsFilter = Response<{
  appointments: Array<AppointmentModel>
}>

type ResponseAppointments = Response<WithFilter<Array<AppointmentModel>>>

type PayloadFilterAppointment = RequestQuery<{
  started_at_start?: string
  started_at_end?: string
}>

type PayloadFilterForDoctorClinic = RequestQuery<{
  date: string
  clinic_id: ID
  doctor_id?: ID
}>

type PayloadFilterForAppointmentFormPatient = RequestQuery<{
  date: string
  patient_id: ID
}>

type FilterForQueueNumberPatient = PayloadFilterAppointment & {
  patient_id: ID
}

type PayloadCreateAppointment = {
  appointment_clinic_id: ID
  doctor_id: ID
  patient_id: ID
  subject: string
  type: string
  started_at: string
  ended_at: string
  remark?: string
}

type PayloadUpdateAppointment = {
  appointment_clinic_id: ID
  doctor_id: ID
  subject: string
  type: string
  started_at: string
  ended_at: string
  remark?: string
}

type PayloadSetReferredDoctor = {
  doctor_id: ID
}

export interface IAppointmentAPI {
  filter: (query: PayloadFilterAppointment) => Promise<ResponseAppointmentsFilter>

  filterForAppointmentFormDoctorClinic: (query: PayloadFilterForDoctorClinic) => Promise<ResponseAppointments>

  filterForAppointmentFormPatient: (query: PayloadFilterForAppointmentFormPatient) => Promise<ResponseAppointments>

  filterForGenerateQueueNumberPatient: (
    query: RequestQuery<FilterForQueueNumberPatient>
  ) => Promise<ResponseAppointments>

  create: (payload: PayloadCreateAppointment) => Promise<ResponseAppointment>

  update: (id: ID, payload: PayloadUpdateAppointment) => Promise<ResponseAppointment>

  get: (id: ID) => Promise<ResponseAppointment>

  setStatus: (id: ID, status: 'approve' | 'cancel') => Promise<ResponseSuccess>

  setReferredDoctor: (id: ID, payload: PayloadSetReferredDoctor) => Promise<ResponseAppointment>
}

const APPOINTMENTS_URL = `${API_URL}/appointments`

const AppointmentAPI: IAppointmentAPI = {
  filter: async (query) => {
    return axios.post(`${APPOINTMENTS_URL}/filter`, query).then((r: AxiosResponse) => r.data)
  },

  filterForAppointmentFormDoctorClinic: async (query) => {
    return axios
      .post(`${APPOINTMENTS_URL}/filter/appointment-form/clinic-doctor`, query)
      .then((r: AxiosResponse) => r.data)
  },

  filterForAppointmentFormPatient: async (query) => {
    return axios.post(`${APPOINTMENTS_URL}/filter/appointment-form/patient`, query).then((r: AxiosResponse) => r.data)
  },

  filterForGenerateQueueNumberPatient: async (query) => {
    return axios
      .post(`${APPOINTMENTS_URL}/filter/queue-number-generate-form/patient`, query)
      .then((r: AxiosResponse) => r.data)
  },

  create: async (payload) => {
    return axios.post(APPOINTMENTS_URL, payload).then((r: AxiosResponse) => r.data)
  },

  update: async (id: ID, payload) => {
    return axios.put(`${APPOINTMENTS_URL}/${id}`, payload).then((r: AxiosResponse) => r.data)
  },

  get: async (id: ID) => {
    return axios.get(`${APPOINTMENTS_URL}/${id}`).then((r: AxiosResponse) => r.data)
  },

  setStatus: async (id, status) => {
    return axios.patch(`${APPOINTMENTS_URL}/${id}/${status}`).then((r: AxiosResponse) => r.data)
  },

  setReferredDoctor: async (id, payload) => {
    return axios.patch(`${APPOINTMENTS_URL}/${id}/referred-doctor`, payload).then((r: AxiosResponse) => r.data)
  },
}

export type {
  PayloadCreateAppointment,
  PayloadFilterAppointment,
  PayloadFilterForAppointmentFormPatient,
  PayloadFilterForDoctorClinic,
  PayloadSetReferredDoctor,
  PayloadUpdateAppointment,
}

export default AppointmentAPI
